import { query } from '@core/db';
import { vnd } from '@core/i18n';
import { renderPageHeader } from '@widgets/KPICard';
import { renderDataTable, type Column } from '@widgets/DataTable';
import { convertQuotationToSO } from '@services/workflow';
import { fmtDateTime } from '@/shared/format';

interface Quotation { id: number; code: string; customer_name: string; total: number; status: string; created_at: string; urgency: string; }

export async function render(): Promise<HTMLElement> {
  const c = document.createElement('div');
  c.appendChild(renderPageHeader('📋', '报价列表 · Quotations', '历史报价 · 一键转销售订单 · 客户跟单'));

  const toolbar = document.createElement('div');
  toolbar.className = 'mb-3 flex gap-2';
  const btnNew = document.createElement('button');
  btnNew.className = 'px-3 py-1.5 rounded-lg text-white text-sm font-semibold shadow-sm';
  btnNew.style.background = 'linear-gradient(135deg, #3b82f6, #1e40af)';
  btnNew.innerHTML = '➕ 新建报价';
  btnNew.onclick = () => { window.location.hash = '#quotationCreate'; };
  toolbar.appendChild(btnNew);
  c.appendChild(toolbar);

  // v358.2 P0 真治 · 主查询稳健 + urgency 单独融合
  const rows = query<Quotation>(`
    SELECT q.id, q.code, COALESCE(c.name, '-') AS customer_name, q.total, q.status, q.created_at, 'normal' AS urgency
    FROM quotations q LEFT JOIN customers c ON c.id = q.customer_id
    WHERE COALESCE(q.deleted_at, '') = ''
    ORDER BY q.id DESC LIMIT 100
  `);
  // urgency 单独查 (列缺时返 [])
  const urgRows = query<{ id: number; urgency: string | null }>(`SELECT id, urgency FROM quotations WHERE COALESCE(deleted_at,'')='' ORDER BY id DESC LIMIT 100`);
  const urgMap = new Map<number, string>();
  for (const r of urgRows) urgMap.set(r.id, r.urgency || 'normal');
  // notes 含 🔥 兜底
  const notesRowsQ = query<{ id: number; notes: string | null }>(`SELECT id, notes FROM quotations WHERE COALESCE(deleted_at,'')='' ORDER BY id DESC LIMIT 100`);
  for (const r of notesRowsQ) {
    if ((r.notes || '').includes('🔥')) urgMap.set(r.id, 'urgent');
  }
  rows.forEach(r => { r.urgency = urgMap.get(r.id) || 'normal'; });
  rows.sort((a, b) => {
    const aU = a.urgency === 'urgent' ? 0 : 1;
    const bU = b.urgency === 'urgent' ? 0 : 1;
    if (aU !== bU) return aU - bU;
    return b.id - a.id;
  });

  // 暴露给 inline onclick (workflow 联动)
  (window as any).__tnConvertQuotation = async (id: number) => {
    if (!confirm('确认转销售订单? / Xác nhận chuyển thành SO?')) return;
    const r = await convertQuotationToSO(id);
    alert(r.message);
    if (r.ok && r.newRecord) {
      window.location.hash = '#salesOrderList';
    } else {
      // 刷新本页
      const h = window.location.hash;
      window.location.hash = '';
      window.location.hash = h;
    }
  };

  const cols: Column<Quotation>[] = [
    { key: 'code', label: '报价号', width: '190px', render: (r) => {
      const urgentChip = r.urgency === 'urgent' ? '<span class="inline-block px-1.5 py-0.5 mr-1 bg-red-600 text-white rounded text-[10px] font-bold animate-pulse" title="加急">🔥</span>' : '';
      return `${urgentChip}<a href="#quotationDetail?id=${r.id}" class="text-blue-700 hover:underline font-medium">${r.code || '-'}</a>`;
    } },
    { key: 'customer_name', label: '客户', render: (r) => `<a href="#quotationDetail?id=${r.id}" class="hover:underline">${r.customer_name || '-'}</a>` },
    { key: 'total', label: '金额', align: 'right', render: (r) => `<span class="font-semibold text-blue-700">${vnd(r.total)}</span>` },
    {
      key: 'status', label: '状态', align: 'center',
      render: (r) => {
        const cls = r.status === 'confirmed' ? 'bg-green-100 text-green-700' : r.status === 'pending' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600';
        return `<span class="inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}">${r.status}</span>`;
      },
    },
    {
      key: 'action', label: '操作', align: 'center',
      render: (r) => {
        if (r.status === 'confirmed') {
          return '<span class="text-xs text-gray-400">已转 SO</span>';
        }
        return `<button onclick="event.stopPropagation();window.__tnConvertQuotation(${r.id})" class="px-2 py-1 rounded text-xs font-semibold text-white" style="background:linear-gradient(135deg,#10b981,#059669)">→ 转 SO</button>`;
      },
    },
    { key: 'created_at', label: '创建', width: '140px', render: (r) => fmtDateTime(r.created_at) },
  ];

  c.appendChild(renderDataTable({ columns: cols, data: rows, emptyText: '暂无报价 · 点 ➕ 新建' }));
  return c;
}
